import { useCallback, useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { SetJourneyRequest, UnnamedJourney } from '@fullstory/proto/fs/services/dx-objects';
import { useFlags, useNotifications } from '@fullstory/react-hooks';
import { getJourneysById, getJourneyAccess, getActiveJourneys } from './selectors';
import { JourneysActions, JourneysRequestActions } from './actions';
import { AppState, AppDispatch } from '../../types';
import { fullstorySelectors } from '../../selectors';
import { makeUseAccess, makeUseCreateAccess } from '../../utils/accessControl';
import { JourneyMapComputation } from '@fullstory/proto/fs/api/metrics/pagemetrics';
import { AppCacheStatus } from '@fullstory/redux-util/utils/appCache/types';
import { isEmpty } from 'lodash';
import { Timestamp } from '@fullstory/proto/google/protobuf';
import { ComputeJourneyMapResponse } from '@fullstory/proto/fs/services/alexa';
import { hashObject } from '@fullstory/util/hash';
import { useRequestState } from '../../utils/hooks';
import { intl } from '@fullstory/intl';
import { Flag } from '@fullstory/proto/fs/shared/flags';
import { SegmentRequestActions } from '../segments';
import { Visibility } from '@fullstory/proto/fs/gcloud/dstore/protoauditstore';
const { getOrgId } = fullstorySelectors;

export const useFetchAllJourneys = (orgId: string, includeArchived: boolean) => {
  const dispatch = useDispatch<AppDispatch>();
  const flags = useFlags();

  const fetchJourneys = useCallback(() => {
    dispatch(JourneysRequestActions.getAllJourneys({ orgId, includeArchived }));
  }, [dispatch, orgId, includeArchived]);

  useEffect(() => {
    if (flags.hasBit(Flag.FeatJourneys)) fetchJourneys();
  }, [fetchJourneys, flags]);
  return fetchJourneys;
};

export const useJourneys = () => {
  const requestStateStatus = useRequestState((state: AppState) => state.journeyMaps.allJourneys);

  return {
    ...requestStateStatus,
    byId: useSelector(getJourneysById),
    activeJourneyMaps: useSelector(getActiveJourneys),
  };
};

/**
 * Returns callbacks to perform mutations on saved journeyMaps
 */
export const useJourneysActions = () => {
  const notifications = useNotifications();
  const dispatch = useDispatch<AppDispatch>();
  const orgId = useSelector(getOrgId);

  return useMemo(
    () => ({
      // We want this to happen transparently to the user, so no notifications are triggered.
      markViewed: (journeyId: string) =>
        dispatch(
          JourneysRequestActions.markJourneyViewed({
            orgId,
            journeyId,
          }),
        ),
      create: async ({ request }: { request: Omit<SetJourneyRequest, 'orgId'> }) => {
        try {
          if (!request.journey) throw new Error('Invalid Journey');
          const response = await dispatch(
            JourneysRequestActions.setJourney({
              ...request,
              orgId,
              journey: {
                ...request.journey,
                id: '',
              },
            }),
          );
          if (!response.journey?.journey) throw new Error('Invalid Journey');
          notifications?.success(
            intl.formatMessage({ defaultMessage: 'Successfully created Journey', id: 'dwf/H+' }),
          );
          if (request.visibility === Visibility.PUBLIC) {
            dispatch(SegmentRequestActions.getAllSegments(orgId, true));
          }

          return response;
        } catch (e) {
          notifications?.error(
            intl.formatMessage({ defaultMessage: 'Failed to create Journey', id: 'FSIMBz' }),
          );
          return null;
        }
      },
      duplicate: async ({ request }: { request: Omit<SetJourneyRequest, 'orgId'> }) => {
        try {
          if (!request.journey) throw new Error('Invalid Journey');
          const response = await dispatch(
            JourneysRequestActions.setJourney({
              ...request,
              orgId,
              journey: {
                ...request.journey,
                id: '',
                name: intl.formatMessage(
                  { defaultMessage: '{journeyName} (copy)', id: 'UGfp/B' },
                  { journeyName: request.journey.name },
                ),
              },
            }),
          );
          if (!response.journey?.journey) throw new Error('Invalid Journey');
          notifications?.success(
            intl.formatMessage({ defaultMessage: 'Successfully duplicated Journey', id: 'IXsEjq' }),
          );
          if (request.visibility === Visibility.PUBLIC) {
            dispatch(SegmentRequestActions.getAllSegments(orgId, true));
          }

          return response;
        } catch (e) {
          notifications?.error(
            intl.formatMessage({ defaultMessage: 'Failed to duplicate Journey', id: 'F89Y5u' }),
          );
          return null;
        }
      },
      update: async ({ request }: { request: Omit<SetJourneyRequest, 'orgId'> }) => {
        try {
          const response = await dispatch(
            JourneysRequestActions.setJourney({
              ...request,
              orgId,
            }),
          );
          if (!response.journey?.journey?.journeyMap?.id) throw new Error('Invalid Journey');
          notifications?.success(
            intl.formatMessage({ defaultMessage: 'Successfully updated Journey', id: 'QgBW+Z' }),
          );
          if (request.visibility === Visibility.PUBLIC) {
            dispatch(SegmentRequestActions.getAllSegments(orgId, true));
          }
          return response;
        } catch (e) {
          notifications?.error(
            intl.formatMessage({ defaultMessage: 'Failed to update Journey', id: 'M5Q3P9' }),
          );
          return null;
        }
      },
      star: (journeyId: string) =>
        dispatch(JourneysRequestActions.starJourney({ orgId, journeyId, starred: true })).catch(
          () =>
            notifications?.error(
              intl.formatMessage({ defaultMessage: 'Failed to star Journey', id: 'yApRRD' }),
            ),
        ),
      unstar: (journeyId: string) =>
        dispatch(JourneysRequestActions.starJourney({ orgId, journeyId, starred: false })).catch(
          () =>
            notifications?.error(
              intl.formatMessage({ defaultMessage: 'Failed to unstar Journey', id: '2YeKJz' }),
            ),
        ),
      archive: async (journeyId: string) => {
        try {
          const response = await dispatch(
            JourneysRequestActions.archiveJourney({
              orgId,
              journeyId,
              archived: true,
            }),
          );
          notifications?.success(
            intl.formatMessage({ defaultMessage: 'Successfully archived Journey', id: 'nCpNSw' }),
          );
          return response;
        } catch (e) {
          notifications?.error(
            intl.formatMessage({ defaultMessage: 'Failed to archive Journey', id: 'T/uXgd' }),
          );
          return null;
        }
      },
      restore: (journeyId: string) =>
        dispatch(JourneysRequestActions.archiveJourney({ orgId, journeyId, archived: false }))
          .then(() =>
            notifications?.success(
              intl.formatMessage({ defaultMessage: 'Successfully restored Journey', id: 'LSZesN' }),
            ),
          )
          .catch(() =>
            notifications?.error(
              intl.formatMessage({ defaultMessage: 'Failed to restore Journey', id: 'RF/uip' }),
            ),
          ),
      createUnnamedJourney: (journey: UnnamedJourney) => {
        const hash = `${hashObject(journey)}`;
        dispatch(JourneysRequestActions.createUnnamedJourney({ hash, journey })).catch(() =>
          console.error('Failed to save temporary journey state'),
        );
        return hash;
      },
    }),
    [dispatch, orgId, notifications],
  );
};

export const useJourneyMapsAccess = makeUseAccess(
  JourneysRequestActions.getAccess,
  getJourneyAccess,
);

export const useJourneyMapsCreateAccess = makeUseCreateAccess(
  JourneysRequestActions.getAccess,
  getJourneyAccess,
);

export type JourneyMapComputationSettings = {
  storageId: string;
  computation: JourneyMapComputation | null;
};

export const useJourneyMapComputationResults = (
  computationToFetch: JourneyMapComputationSettings,
) => {
  const journeyMapData = useSelector((state: AppState) => state.journeyMaps.journeyMetrics);
  const dispatch = useDispatch<AppDispatch>();
  const storageId = computationToFetch.storageId;

  // The ResourceCacheButton shows the status with the highest priority (HasData > Fresh). Because of this we can get into a state where 1 or more
  // storage ids has new data in the app cache but the other storage ids have fresh data. If the user clicks the cache button, we only want to
  // swap the cache for the stale storage id. Swapping the cache for the other ids will inadvertantly wipe out the cached data.
  const forceCacheSwap = useCallback(() => {
    if (journeyMapData[storageId]?.status == AppCacheStatus.HasData) {
      dispatch(JourneysActions.appCacheSwap(storageId));
    }
  }, [storageId, dispatch, journeyMapData]);

  const { results, status, timestamp } = useMemo(() => {
    if (isEmpty(journeyMapData) || isEmpty(computationToFetch)) return {};

    const response = {
      data: journeyMapData[storageId]?.data,
      status: journeyMapData[storageId]?.status,
    };

    return {
      results: {
        data: response.data,
      },
      status: response.status,
      timestamp: Timestamp.toDate(response?.data?.computedAt),
    };
  }, [computationToFetch, storageId, journeyMapData]);

  const isValid = !!computationToFetch.computation;

  const data: ComputeJourneyMapResponse = useMemo(() => {
    if (
      isValid &&
      results &&
      results.data &&
      (status == AppCacheStatus.Fresh || status == AppCacheStatus.HasData)
    ) {
      return results.data;
    }
    return {
      journeyMap: null,
      computedAt: Timestamp.nil,
      backgroundRefId: '',
      sampleResults: null,
    };
  }, [results, isValid, status]);

  return {
    ...data,
    status: status ?? AppCacheStatus.Initial,
    /**
     * Function that, when called, triggers a cache swap of the page flow computation(s) that is out of date.
     */
    forceCacheSwap,
    timestamp,
  };
};
